import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { Button, Loader } from 'ncoded-component-library';
import Dropzone from 'components/Dropzone';
import TextareaEmailsField from 'components/TextareaEmailsField';
import validations from 'validations';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import {
  Enterprise,
  ENTERPRISE_STATUS,
  ENTERPRISE_TYPE,
  UpdateEnterpriseValue,
} from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useNavigate } from 'react-router-dom';
import { SUCCESS_MESSAGES } from 'request-responses';
import { getArrayOfEmails } from 'utils';
import { ADMIN_DASHBOARD_ENTERPRISE_ROUTE } from 'data/global';
import RadioGroupField from 'components/RadioGroupField';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './EnterpriseForm.styles.scss';
import './EnterpriseForm.styles.responsive.scss';

type EnterpriseFormProps = {
  className?: string;
  enterprise: Enterprise;
  updateEnterprise?: (enterprise: Enterprise) => void;
};

const EnterpriseForm: React.FC<EnterpriseFormProps> = (props) => {
  const { className, enterprise, updateEnterprise } = props;

  const { _id, brandName, logo, principals, supports, status, type } =
    enterprise || {};

  const [files, setFiles] = useState([]);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const initialValues: Partial<UpdateEnterpriseValue> = useMemo(
    () => ({
      brandName,
      principalEmails: (principals || []).map(({ email }) => email),
      supportEmails: (supports || []).map(({ email }) => email),
      type: type || ENTERPRISE_TYPE.GENERAL,
    }),
    [brandName, principals, supports, type],
  );

  const shouldPublish = status === ENTERPRISE_STATUS.DRAFTED;

  const classes = classNames('enterprise-form', className);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const createSelectOptions = useCreateSelectOptions();

  const typeOptions = useMemo(
    () => createSelectOptions(Object.values(ENTERPRISE_TYPE)),
    [createSelectOptions],
  );

  const handleOnSubmit = useCallback(
    async (
      values: UpdateEnterpriseValue & {
        principalEmailsUnsubmited: string;
        supportEmailsUnsubmited: string;
      },
    ) => {
      const {
        brandName,
        principalEmails,
        principalEmailsUnsubmited,
        supportEmails,
        supportEmailsUnsubmited,
        type,
      } = values;

      const finalPrincipalEmails = getArrayOfEmails(
        principalEmails,
        principalEmailsUnsubmited,
      );

      const finalSupportEmails = getArrayOfEmails(
        supportEmails,
        supportEmailsUnsubmited,
      );

      try {
        const apiCall = shouldPublish
          ? api.enterprises.publishEnterprise
          : api.enterprises.updateEnterprise;

        const { data } = await apiCall(_id, {
          brandName,
          principalEmails: finalPrincipalEmails,
          supportEmails: finalSupportEmails,
          ...(shouldPublish && { type }),
        });

        popSuccess(
          t(
            shouldPublish
              ? SUCCESS_MESSAGES.CREAT_BRAND
              : SUCCESS_MESSAGES.EDIT_BRAND,
          ),
        );

        updateEnterprise?.(data);

        navigate(
          shouldPublish ? ADMIN_DASHBOARD_ENTERPRISE_ROUTE : 'manage-people',
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [
      _id,
      shouldPublish,
      t,
      navigate,
      popServerError,
      popSuccess,
      updateEnterprise,
    ],
  );

  const uploadLogo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const data = await api.enterprises.uploadLogo(
        file,
        fileName,
        _id,
        config,
      );
      setFiles([data]);
      return data;
    },
    [_id],
  );

  const deleteLogo: DropzoneDelete = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId },
      } = await api.enterprises.deleteLogo(fileName, file._id, _id);
      return fileId;
    },
    [_id],
  );

  useEffect(() => {
    setFiles(() => (logo ? [logo] : []));
  }, [logo]);

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      render={({ handleSubmit, submitting, invalid }) => {
        return (
          <form onSubmit={handleSubmit} className={classes}>
            {!enterprise && <Loader />}

            <div>
              <div className="column">
                <Field
                  name="brandName"
                  label={t('brandName')}
                  component={InputField}
                  required
                  description={t('brandNameDescription')}
                  validate={validations.required()}
                />

                {shouldPublish && (
                  <RadioGroupField
                    name="type"
                    className="radio"
                    required
                    label={t('dataset')}
                    description={t('selectDataset')}
                    options={typeOptions}
                  />
                )}
              </div>

              <div className="column">
                <Field
                  name="principalEmails"
                  label={t('entEmailLabel')}
                  component={TextareaEmailsField}
                  description={t('entEmailLabelDescription')}
                />
                <Field
                  name="supportEmails"
                  label={t('entSupportEmailLabel')}
                  component={TextareaEmailsField}
                  description={t('entSupportEmailLabelDescription')}
                />
              </div>

              <div>
                <label className="required">{t('logo')}</label>
                <Dropzone
                  accept="image/*"
                  maxFiles={1}
                  required
                  fileName="logo"
                  files={files}
                  uploadRequest={uploadLogo}
                  deleteRequest={deleteLogo}
                  replaceFile
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting || invalid || !files.length}
            >
              {t(shouldPublish ? 'createBrand' : 'editBrand')}
            </Button>
          </form>
        );
      }}
    />
  );
};

export default EnterpriseForm;
